import { Button, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import * as React from "react";

import { LoadingWrapper } from "../../../components/LoadingWrapper";
import { Helpers } from "../../../utils/helpers";
import { fetchTournamentDetail, TournamentDetailResponse, TournamentEnvironmentDetail as TournamentEnvironmentDetailModel } from "../api";
import { formatTournamentDuration, renderTournamentStatus, renderTournamentValue, TournamentEmptyState, TournamentJsonBlock, TournamentKeyValueGrid, TournamentPageHeader, TournamentSection, TournamentSourceNotice } from "../components";

export const TournamentEnvironmentDetail = (props: any) => {
    const environmentId = props.match?.params?.environmentId;
    const [response, setResponse] = React.useState<TournamentDetailResponse<TournamentEnvironmentDetailModel> | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        document.title = Helpers.documentTitle("Tournament Environment");
        loadDetail();
    }, [environmentId]);

    const loadDetail = () => {
        setLoading(true);
        setError(null);

        fetchTournamentDetail<TournamentEnvironmentDetailModel>(`/api/tournaments/environments/${environmentId}`, props.activeEnv)
            .then(data => {
                setResponse(data);
            })
            .catch(err => {
                setError(err?.response?.data?.message || err?.message || "Couldn't load tournament environment detail.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const item = response?.data.item;

    return <div className="contentPadding">
        <TournamentPageHeader
            title="Tournament / Environment / Detail"
            action={<Button disableElevation variant="contained" color="primary" onClick={loadDetail}>Refresh</Button>}
        />

        <div style={{ marginTop: 16 }}>
            <TournamentSourceNotice meta={response?.meta} />
        </div>

        <LoadingWrapper loading={loading} error={error}>
            {!response?.data.found || !item ? <TournamentEmptyState
                title="Environment not found"
                description={`No tournament environment is available for ID ${environmentId}.`}
            /> : <React.Fragment>
                <TournamentSection
                    title="Summary"
                    action={renderTournamentStatus(item.status)}
                >
                    <TournamentKeyValueGrid
                        items={[
                            { label: "Environment ID", value: item.id },
                            { label: "Name", value: renderTournamentValue(item.name) },
                            { label: "Type", value: renderTournamentValue(item.type) },
                            { label: "Version", value: renderTournamentValue(item.version) },
                            { label: "Linked layouts", value: item.linkedLayoutsCount },
                            { label: "Battle usage", value: item.battleUsageCount },
                            { label: "Updated at", value: item.updatedAt ? Helpers.formatDateWithTime(item.updatedAt, true) : "n/a" },
                        ]}
                    />
                </TournamentSection>

                <TournamentSection title="Rules">
                    <TournamentKeyValueGrid
                        items={Object.entries(item.rules || {}).map(([label, value]) => ({
                            label,
                            value: Array.isArray(value) ? value.join(", ") : renderTournamentValue(value),
                        }))}
                    />
                </TournamentSection>

                <TournamentSection title="Modifiers">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Key</TableCell>
                                <TableCell>Value</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {item.modifiers.map((modifier, index) => <TableRow key={`${item.id}-modifier-${index}`}>
                                <TableCell>{renderTournamentValue(modifier.key)}</TableCell>
                                <TableCell>{renderTournamentValue(modifier.value)}</TableCell>
                            </TableRow>)}
                        </TableBody>
                    </Table>
                </TournamentSection>

                <TournamentSection title="Linked Layouts">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Layout</TableCell>
                                <TableCell>Difficulty</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Usage count</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {item.linkedLayouts.map(layout => <TableRow key={layout.id}>
                                <TableCell>{renderTournamentValue(layout.name)}</TableCell>
                                <TableCell>{renderTournamentValue(layout.difficulty)}</TableCell>
                                <TableCell>{renderTournamentStatus(layout.status)}</TableCell>
                                <TableCell>{layout.usageCount}</TableCell>
                                <TableCell>
                                    <Button size="small" onClick={() => props.history.push(`/tournament/layouts/${layout.id}`)}>
                                        Open
                                    </Button>
                                </TableCell>
                            </TableRow>)}
                        </TableBody>
                    </Table>
                </TournamentSection>

                <TournamentSection title="Recent Battles">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Battle ID</TableCell>
                                <TableCell>Tournament</TableCell>
                                <TableCell>Round</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {item.recentBattles.map(battle => <TableRow key={battle.id}>
                                <TableCell>{battle.id}</TableCell>
                                <TableCell>{renderTournamentValue(battle.tournamentName)}</TableCell>
                                <TableCell>{renderTournamentValue(battle.round)}</TableCell>
                                <TableCell>{renderTournamentStatus(battle.status)}</TableCell>
                                <TableCell>{formatTournamentDuration(battle.durationSeconds)}</TableCell>
                                <TableCell>
                                    <Button size="small" onClick={() => props.history.push(`/tournament/battle-logs/${battle.id}`)}>
                                        Open
                                    </Button>
                                </TableCell>
                            </TableRow>)}
                        </TableBody>
                    </Table>
                </TournamentSection>

                <TournamentJsonBlock title="Raw Payload" data={item.rawPayload || {}} />
            </React.Fragment>}
        </LoadingWrapper>
    </div>;
};
